/**
 * feAI – Frontend
 * src/components/layout/AppShell.tsx
 *
 * Shell principale con sidebar (drag & drop, comprimibile).
 * Brand: FuturEnergy Branding Manual 2026
 * Font: DM Sans | Palette: #00A157 · #64BE6F · #008D4C · #B4FF00
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, typography } from '../../lib/designTokens';
import { FuturEnergyLogo } from '../brand/FuturEnergyLogo';
import { InactivityGuard } from '../auth/InactivityGuard';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import './AppShell.css';

function parseJwt(token: string) {
  try { return JSON.parse(atob(token.split('.')[1]!)) as Record<string, unknown>; }
  catch { return null; }
}

interface NavItem {
  id:       string;
  icon:     string;
  label:    string;
  children?: NavItem[];
}

const PERMISSIONS_NAV_ITEM: NavItem = { id: 'admin/permissions', icon: '🔐', label: 'Ruoli e Permessi' };

// Nota: Clienti, Lead, Vendite, Contratti, Agenti, Pratiche appartengono a utilityos (OS), non ad AI
// Dashboard unificata: la vecchia DashboardPage è stata rimossa e sostituita da
// AgentiDashboardPage (che ora è servita anche a /dashboard). Il titolo della
// pagina varia per ruolo: DashBoard Admin / DashBoard Inserter / DashBoard Agenti.
const BASE_NAV_ITEMS: NavItem[] = [
  { id: 'dashboard',          icon: '⊞',  label: 'Dashboard' },
  { id: 'users',              icon: '👥', label: 'Utenti' },
  {
    id: 'report',
    icon: '📊',
    label: 'REPORT',
    children: [
      { id: 'report-accessi',     icon: '🔑', label: 'Accessi FW' },
      { id: 'page-access-report', icon: '🖱️', label: 'Navigazione Pagine' },
      { id: 'mail-report',        icon: '📧', label: 'Mail' },
      { id: 'report-apparound',   icon: '📊', label: 'Apparound' },
    ],
  },
  { id: 'archiviati',         icon: '📦', label: 'Archiviati' },
  { id: 'retention',          icon: '🏆', label: 'Retention' },
  { id: 'gerarchia-agenti',  icon: '🧑‍💼', label: 'Gerarchia Agenti' },
  { id: 'partite-aperte',     icon: '🔍', label: 'Partite Aperte' },
];

function isAdminFromPayload(payload: Record<string, unknown> | null): boolean {
  if (!payload) return false;
  const role = String(payload['role'] ?? '').toUpperCase().trim();
  const legacyRoleId = payload['legacy_role_id'];
  return role === 'ADM' || role === 'ADMIN' || legacyRoleId === 1;
}

const STORAGE_KEY = 'feai_sidebar_order';

interface Props {
  activePage: string;
  onNavigate: (page: string) => void;
  children: React.ReactNode;
}

export const AppShell: React.FC<Props> = ({ activePage, onNavigate, children }) => {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();
  const token = localStorage.getItem('feai_access_token') ?? '';
  const payload = parseJwt(token);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);
  const { canViewPage, permissions } = usePermissions();

  // Auto-expand parent groups if a child page is active
  function getInitialExpanded(): Set<string> {
    const expanded = new Set<string>();
    for (const item of BASE_NAV_ITEMS) {
      if (item.children && item.children.some(child => child.id === activePage)) {
        expanded.add(item.id);
      }
    }
    return expanded;
  }

  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => getInitialExpanded());

  function toggleExpand(parentId: string) {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }

  function getBaseItems(): NavItem[] {
    if (!permissions) return [];
    const allItems = isAdminFromPayload(payload)
      ? [...BASE_NAV_ITEMS, PERMISSIONS_NAV_ITEM]
      : BASE_NAV_ITEMS;
    return allItems.filter(item => {
      if (item.children) {
        // Parent is visible if at least one child is visible
        return item.children.some(child => canViewPage(child.id));
      }
      // Dashboard unificata: visibile se l'utente ha accesso a
      // "dashboard" (nuova) o a "agenti-dashboard" (retrocompatibilità)
      if (item.id === 'dashboard') {
        return canViewPage('dashboard') || canViewPage('agenti-dashboard');
      }
      return canViewPage(item.id);
    });
  }

  function getOrderedItems(): NavItem[] {
    const base = getBaseItems();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return base;
      const savedIds = JSON.parse(saved) as string[];
      const baseIds = new Set(base.map(i => i.id));
      const ordered = savedIds.filter(id => baseIds.has(id)).map(id => base.find(i => i.id === id)!);
      const savedSet = new Set(savedIds);
      const newItems = base.filter(i => !savedSet.has(i.id));
      return [...ordered, ...newItems];
    } catch {
      return base;
    }
  }

  const [items, setItems] = useState<NavItem[]>(() => getOrderedItems());

  useEffect(() => {
    setItems(getOrderedItems());
  }, [permissions]);

  function handleDragStart(index: number) {
    dragItem.current = index;
    setDragIndex(index);
  }

  function handleDragEnter(index: number) {
    if (dragItem.current === null || dragItem.current === index) return;
    setOverIndex(index);
    const newItems = [...items];
    const [draggedItem] = newItems.splice(dragItem.current, 1);
    newItems.splice(index, 0, draggedItem);
    dragItem.current = index;
    setItems(newItems);
  }

  function handleDragEnd() {
    dragItem.current = null;
    setDragIndex(null);
    setOverIndex(null);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function salvaOrdine() {
    const ids = items.map(i => i.id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    const btn = document.querySelector('.save-order-btn');
    if (btn) {
      btn.textContent = '✅ Salvato!';
      setTimeout(() => { btn.textContent = '💾 Salva ordine'; }, 1500);
    }
    console.log('Ordine salvato:', ids);
  }

  function handleLogout() {
    authLogout();
    navigate('/login', { replace: true });
  }

  function handleItemClick(item: NavItem) {
    if (item.children) {
      // Toggle expand/collapse for parent group items
      toggleExpand(item.id);
    } else if (item.id === 'dashboard') {
      // Dashboard unificata: naviga alla pagina per cui l'utente ha permesso
      // (retrocompatibilità con "agenti-dashboard" per chi non ha ancora il permesso "dashboard")
      if (canViewPage('dashboard')) {
        onNavigate('dashboard');
      } else if (canViewPage('agenti-dashboard')) {
        onNavigate('agenti-dashboard');
      } else {
        onNavigate('dashboard');
      }
    } else if (item.id === 'report-apparound') {
      navigate('/report-apparound');
    } else if (item.id === 'report-accessi') {
      navigate('/report-accessi');
    } else {
      onNavigate(item.id);
    }
  }

  /** Check if any child of a parent group is the active page */
  function isChildActive(parent: NavItem): boolean {
    if (!parent.children) return false;
    return parent.children.some(child => activePage === child.id);
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      fontFamily: typography.fontFamily,
      background: colors.offWhite,
    }}>
      <InactivityGuard onLogout={handleLogout} />

      <aside className={`appshell-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(p => !p)}
          title={sidebarOpen ? 'Chiudi sidebar' : 'Apri sidebar'}
        >
          {sidebarOpen ? '◀' : '▶'}
        </button>

        <div className="sidebar-logo-area">
          <FuturEnergyLogo variant="positive" size={sidebarOpen ? 150 : 36} />
          <div className="sidebar-logo-sub">AI Portal</div>
        </div>

        <nav className="sidebar-nav">
          {items.map((item, index) => {
            const isActive = activePage === item.id;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedParents.has(item.id);
            const childActive = isChildActive(item);

            if (hasChildren) {
              // Render parent group item with expandable sub-menu
              const visibleChildren = item.children!.filter(child => canViewPage(child.id));
              return (
                <div key={item.id} className="sidebar-nav-group">
                  <div
                    className={`sidebar-nav-item ${childActive ? 'active' : ''} ${dragIndex === index ? 'dragging' : ''} ${overIndex === index ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onClick={() => handleItemClick(item)}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <span className="drag-handle">⠿</span>
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    {sidebarOpen && (
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        {isExpanded ? '▾' : '▸'}
                      </span>
                    )}
                  </div>
                  {isExpanded && sidebarOpen && (
                    <div className="sidebar-subnav">
                      {visibleChildren.map(child => {
                        const isChildActivePage = activePage === child.id;
                        return (
                          <div
                            key={child.id}
                            className={`sidebar-subnav-item ${isChildActivePage ? 'active' : ''}`}
                            onClick={() => handleItemClick(child)}
                          >
                            <span className="subnav-icon">{child.icon}</span>
                            <span className="nav-label">{child.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular item (no children)
            return (
              <div
                key={item.id}
                className={`sidebar-nav-item ${isActive ? 'active' : ''} ${dragIndex === index ? 'dragging' : ''} ${overIndex === index ? 'drag-over' : ''}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onClick={() => handleItemClick(item)}
                title={!sidebarOpen ? item.label : undefined}
              >
                <span className="drag-handle">⠿</span>
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <>
              {payload && (
                <div className="sidebar-user-info">
                  <div className="sidebar-user-name">
                    {[String(payload['last_name'] ?? ''), String(payload['first_name'] ?? '')].filter(Boolean).join(' ') || String(payload['username'] ?? '—').replace(/@aziendaonline$/i, '')}
                  </div>
                  <div title={String(payload['username'] ?? '').split('@')[0] ?? ''}>
                    {String(payload['nome_ruolo'] ?? payload['role'] ?? '—')}
                  </div>
                </div>
              )}
              <button className="save-order-btn" onClick={salvaOrdine}>
                💾 Salva ordine
              </button>
              <div style={{ height: 8 }} />
            </>
          )}
          <button className="logout-btn" onClick={handleLogout}>
            ← Esci
          </button>
        </div>
      </aside>

      <main className="appshell-main">
        {children}
      </main>
    </div>
  );
};
